// src/services/availabilityService.js

import dayjs from 'dayjs';
import availabilityApi from '@/apis/availability';

/**
 * Get availability for team members/admins
 * @param {string|{ from: string, to: string }} monthOrRange - YYYY-MM (legacy, days `{ day, type }`) or `{ from, to }` en YYYY-MM-DD (réponse avec `days: [{ date, type }]`, plage bornée côté API)
 * @param {string|null} teamId - Optional team ID
 */
export const getAvailability = async (monthOrRange, teamId = null) => {
  try {
    const params =
      typeof monthOrRange === 'string' ? { month: monthOrRange } : { ...monthOrRange };
    if (teamId) {
      params.team_id = teamId;
    }
    const response = await availabilityApi.getAvailability(params);
    // get() returns res.data, so response is already { code, data, message }
    if (response?.code === 200) {
      return {
        success: true,
        data: response.data?.availabilities || [],
      };
    }
    return {
      success: false,
      error: response?.message || 'Failed to fetch availability',
    };
  } catch (error) {
    console.error('[AvailabilityService] Error fetching availability:', error);
    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Failed to fetch availability',
    };
  }
};

/**
 * Get availability for current freelancer
 * @param {string} month - Format: YYYY-MM
 * @param {string} teamId - Optional team ID
 */
export const getMyAvailability = async (month, teamId = null) => {
  try {
    const params = { month };
    if (teamId) {
      params.team_id = teamId;
    }
    const response = await availabilityApi.getMyAvailability(params);
    // get() returns res.data, so response is already { code, data, message }
    if (response?.code === 200) {
      return {
        success: true,
        data: {
          availabilities: response.data?.availabilities || [],
          active_mission_intervals: response.data?.active_mission_intervals || [],
        },
      };
    }
    return {
      success: false,
      error: response?.message || 'Failed to fetch availability',
    };
  } catch (error) {
    console.error('[AvailabilityService] Error fetching my availability:', error);
    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Failed to fetch availability',
    };
  }
};

/**
 * Save availability
 * @param {Object} data - { team_id, month, days: [{day: number, type: 'full'|'half'}] }
 */
export const saveAvailability = async (data) => {
  try {
    const response = await availabilityApi.saveAvailability(data);
    console.log('[AvailabilityService] Save response:', response);
    // post() returns res.data, so response is already { code, data, message }
    if (response?.code === 200) {
      return {
        success: true,
        data: response.data?.availability,
      };
    }
    return {
      success: false,
      error: response?.message || 'Failed to save availability',
    };
  } catch (error) {
    console.error('[AvailabilityService] Error saving availability:', error);
    return {
      success: false,
      error: error?.response?.data?.message || error?.message || 'Failed to save availability',
    };
  }
};

/**
 * Convert availability days to timesheet format for DailyTimesheet component
 * @param {Array} days - [{day: number, type: 'full'|'half'|'unavailable'}]
 * @param {string} month - Format: YYYY-MM
 */
export const convertAvailabilityToTimesheet = (days = [], month) => {
  if (!month) return [];
  
  const [year, monthNum] = month.split('-').map(Number);
  
  return days.map((d) => {
    const date = dayjs(`${year}-${String(monthNum).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`);
    const isUnavailable = d.type === 'unavailable';
    return {
      key: `daily-${date.format('YYYY-MM-DD')}`,
      type: 'daily',
      entryType: 'daily',
      date: date.format('YYYY-MM-DD'),
      days: d.type === 'full' ? 1 : d.type === 'half' ? 0.5 : 0,
      availabilityType: isUnavailable ? 'unavailable' : undefined,
      description: '',
    };
  });
};

/**
 * Convert timesheet format to availability days
 * @param {Array} timesheetData - Array from DailyTimesheet component
 * @param {string} month - Format: YYYY-MM
 */
export const convertTimesheetToAvailability = (timesheetData = [], month) => {
  if (!month) return [];
  
  const [year, monthNum] = month.split('-').map(Number);
  
  return timesheetData
    .filter((entry) => {
      if (!entry.date) return false;
      const entryDate = dayjs(entry.date);
      return entryDate.year() === year && entryDate.month() + 1 === monthNum;
    })
    .map((entry) => {
      const entryDate = dayjs(entry.date);
      let type = 'half';
      if (entry.availabilityType === 'unavailable' || entry.days === 0) {
        type = 'unavailable';
      } else if (entry.days === 1) {
        type = 'full';
      }
      return {
        day: entryDate.date(),
        type,
      };
    });
};

/**
 * Send availability reminder email(s) (team members only).
 * Rate limit: 1 reminder every 3 days per (freelancer, team).
 * @param {Object} data - {
 *   team_id: string,
 *   freelancer_ids?: string[],
 *   freelancer_id?: string,
 *   from?: string,
 *   to?: string,
 *   subject?: string
 * }
 * @returns {Promise<{ success: boolean, error?: string, code?: number, data?: { sent, skipped, failed, last_availability_reminder_sent_at } }>}
 */
export const remindAvailability = async (data) => {
  try {
    const response = await availabilityApi.remindAvailability(data);
    if (response?.code === 200) {
      return {
        success: true,
        data: response.data,
      };
    }
    if (response?.code === 429) {
      return {
        success: false,
        error: response?.message || 'Reminder already sent recently',
        code: 429,
        data: response?.data,
      };
    }
    return {
      success: false,
      error: response?.message || 'Failed to send reminder',
      code: response?.code,
    };
  } catch (error) {
    const errData = error?.response?.data;
    return {
      success: false,
      error: errData?.message || error?.message || 'Failed to send reminder',
      code: errData?.code ?? 500,
    };
  }
};

export default {
  getAvailability,
  getMyAvailability,
  saveAvailability,
  remindAvailability,
  convertAvailabilityToTimesheet,
  convertTimesheetToAvailability,
};

